import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import NumberBox from "devextreme-react/number-box";
import Button from "devextreme-react/button";
import TextArea from "devextreme-react/text-area";
import DateBox from "devextreme-react/date-box";
import { confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import { selectEvironmentStored, useAppSelector } from "@app/state";

import {
  generateWhatsNewRequest,
  readWhatsNewRequest,
  runProductEvolutionApi,
  saveWhatsNewRequest,
} from "./WhatsNew";
import type { TWhatsNewEntry, TWhatsNewGeneration } from "./WhatsNew";

// We import styles
import "./super.scss";

interface TBuildFile {
  path: string;
  checked: boolean;
}

type TAgentProjectInfo = {
  setupsDir?: string;
  clientSwscPath?: string;
  buildpathName?: string;
  versionPath?: string;
  buildFiles?: TBuildFile[];
  version?: {
    major: number;
    minor: number;
    month: number;
    day: number;
    source?: string;
  };
};

const prettyJson = (value: unknown) => JSON.stringify(value, null, 2);

export const SwsBuilder = () => {
  // Resolve client code dynamically from the OrgMan selected tree node environment
  const currentEnvData = useAppSelector(selectEvironmentStored);
  const clientCode = currentEnvData?.PERSEED; // e.g. "SIG", "DAN"
  const clientFriendlyName = currentEnvData?.LICENSE_SHORT_NAME;

  // Version controls
  const [versionMajor, setVersionMajor] = useState<number>(1);
  const [versionMinor, setVersionMinor] = useState<number>(16);
  const [versionMonth, setVersionMonth] = useState<number>(1);
  const [versionDay, setVersionDay] = useState<number>(1);

  const [baselineVersion, setBaselineVersion] = useState<{
    major: number;
    minor: number;
    month: number;
    day: number;
  } | null>(null);

  const [versionLoaded, setVersionLoaded] = useState<boolean>(false);

  const [buildStatus, setBuildStatus] = useState<"IDLE" | "RUNNING" | "SUCCESS" | "FAILED">("IDLE");
  const [activeBuildId, setActiveBuildId] = useState<string | null>(null);
  const [consoleLogs, setConsoleLogs] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"progress" | "logs" | "whatsnew">("progress");
  const [agentConnected, setAgentConnected] = useState<boolean>(false);
  const [buildFiles, setBuildFiles] = useState<TBuildFile[]>([]);
  const [loadingProject, setLoadingProject] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<string>("");
  const [whatsNewEntry, setWhatsNewEntry] = useState<TWhatsNewEntry | null>(null);
  const [whatsNewDescription, setWhatsNewDescription] = useState<string>("");
  const [whatsNewLoading, setWhatsNewLoading] = useState<boolean>(false);
  const [whatsNewSaving, setWhatsNewSaving] = useState<boolean>(false);
  const [whatsNewError, setWhatsNewError] = useState<string>("");
  const [whatsNewGenerating, setWhatsNewGenerating] = useState(false);
  const [whatsNewGeneration, setWhatsNewGeneration] =
    useState<TWhatsNewGeneration | null>(null);
  const [generatedDescription, setGeneratedDescription] = useState<string | null>(null);
  const [generatedWasEdited, setGeneratedWasEdited] = useState(false);
  const [preGenerationDescription, setPreGenerationDescription] = useState("");
  const [whatsNewStartRequired, setWhatsNewStartRequired] = useState(false);
  const [whatsNewStartAt, setWhatsNewStartAt] = useState<Date | null>(null);

  const consoleRef = useRef<HTMLDivElement | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const whatsNewLoadRef = useRef(0);
  const whatsNewGenerateRef = useRef(0);
  const whatsNewGenerationAbortRef = useRef<AbortController | null>(null);

  const appendLog = useCallback((message: string) => {
    setConsoleLogs(prev => prev + message);
  }, []);

  const appendDebug = useCallback((label: string, data: Record<string, unknown>) => {
    appendLog(`[Debug] ${label}: ${prettyJson(data)}\n`);
  }, [appendLog]);

  // Poll agent connection status
  useEffect(() => {
    const checkAgentStatus = async () => {
      try {
        const response = await fetch("/rest/api/swsbuilder/v2/moa/agent-status");
        const data = await response.json();
        setAgentConnected(!!data.connected);
      } catch (e) {
        setAgentConnected(false);
      }
    };
    checkAgentStatus();
    const interval = setInterval(checkAgentStatus, 8000);
    return () => {
      clearInterval(interval);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Auto-scroll console logs
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleLogs]);

  // Reset states on clientCode change
  useEffect(() => {
    setBaselineVersion(null);
    setVersionLoaded(false);
    setBuildFiles([]);
    setBuildStatus("IDLE");
    setConsoleLogs("");
    setWhatsNewEntry(null);
    setWhatsNewDescription("");
    setWhatsNewError("");
    setWhatsNewGeneration(null);
    setGeneratedDescription(null);
    setGeneratedWasEdited(false);
    setWhatsNewStartRequired(false);
    setWhatsNewStartAt(null);
    setWhatsNewGenerating(false);
    whatsNewGenerateRef.current += 1;
    whatsNewGenerationAbortRef.current?.abort();
    whatsNewGenerationAbortRef.current = null;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
  }, [clientCode]);

  // Version Comparison Helpers
  const versionToNumber = (v: { major: number; minor: number; month: number; day: number }) => {
    return v.major * 1000000 + v.minor * 10000 + v.month * 100 + v.day;
  };

  const currentEnteredVersion = useMemo(() => {
    return {
      major: versionMajor,
      minor: versionMinor,
      month: versionMonth,
      day: versionDay
    };
  }, [versionMajor, versionMinor, versionMonth, versionDay]);

  const currentVersionCode = useMemo(
    () => `${versionMajor}.${versionMinor}.${versionMonth}.${versionDay}`,
    [versionMajor, versionMinor, versionMonth, versionDay]
  );

  useEffect(() => {
    whatsNewGenerateRef.current += 1;
    whatsNewGenerationAbortRef.current?.abort();
    whatsNewGenerationAbortRef.current = null;
    setWhatsNewGenerating(false);
    setWhatsNewStartRequired(false);
    setWhatsNewStartAt(null);
    if (!versionLoaded) {
      return () => {
        whatsNewGenerateRef.current += 1;
        whatsNewGenerationAbortRef.current?.abort();
        whatsNewGenerationAbortRef.current = null;
      };
    }
    const requestId = ++whatsNewLoadRef.current;
    setWhatsNewGeneration(null);
    setGeneratedDescription(null);
    setGeneratedWasEdited(false);
    setWhatsNewLoading(true);
    setWhatsNewError("");
    runProductEvolutionApi<TWhatsNewEntry | null, { versionCode: string }>(
      readWhatsNewRequest,
      { versionCode: currentVersionCode }
    )
      .then((entry) => {
        if (requestId !== whatsNewLoadRef.current) return;
        setWhatsNewEntry(entry);
        setWhatsNewDescription(entry?.description || "");
        if (!entry) setActiveTab("whatsnew");
      })
      .catch((error) => {
        if (requestId !== whatsNewLoadRef.current) return;
        setWhatsNewEntry(null);
        setWhatsNewDescription("");
        setWhatsNewError(
          error instanceof Error ? error.message : String(error)
        );
      })
      .finally(() => {
        if (requestId === whatsNewLoadRef.current) setWhatsNewLoading(false);
      });
    return () => {
      whatsNewGenerateRef.current += 1;
      whatsNewGenerationAbortRef.current?.abort();
      whatsNewGenerationAbortRef.current = null;
    };
  }, [currentVersionCode, versionLoaded]);

  const handleGenerateWhatsNew = async () => {
    if (
      whatsNewGenerating ||
      buildStatus === "RUNNING" ||
      loadingProject ||
      whatsNewLoading ||
      whatsNewSaving
    ) {
      return;
    }
    const requestId = ++whatsNewGenerateRef.current;
    const abortController = new AbortController();
    whatsNewGenerationAbortRef.current = abortController;
    setWhatsNewGenerating(true);
    setWhatsNewError("");
    try {
      const generation = await runProductEvolutionApi<
        TWhatsNewGeneration,
        { versionCode: string; existingText: string; startAt?: string }
      >(generateWhatsNewRequest, {
        versionCode: currentVersionCode,
        existingText: whatsNewDescription,
        ...(whatsNewStartAt
          ? { startAt: whatsNewStartAt.toISOString() }
          : {}),
      }, abortController.signal);
      if (requestId !== whatsNewGenerateRef.current) return;
      setWhatsNewGeneration(generation);
      setWhatsNewStartRequired(false);
      if (generation.draft && !generation.preserveExistingText) {
        setPreGenerationDescription(whatsNewDescription);
        setWhatsNewDescription(generation.draft);
        setGeneratedDescription(generation.draft);
        setGeneratedWasEdited(false);
      }
    } catch (error) {
      if (requestId !== whatsNewGenerateRef.current) return;
      const message = error instanceof Error ? error.message : String(error);
      setWhatsNewError(message);
      if (/WHATSNEW_START_REQUIRED|release window start/i.test(message)) {
        setWhatsNewStartRequired(true);
      }
    } finally {
      if (whatsNewGenerationAbortRef.current === abortController) {
        whatsNewGenerationAbortRef.current = null;
      }
      if (requestId === whatsNewGenerateRef.current) setWhatsNewGenerating(false);
    }
  };

  const versionWarning = useMemo(() => {
    if (!baselineVersion) return "";
    const currentNum = versionToNumber(currentEnteredVersion);
    const baselineNum = versionToNumber(baselineVersion);
    if (currentNum < baselineNum) {
      return `Versiunea introdusă (${versionMajor}.${versionMinor}.${versionMonth}.${versionDay}) este mai mică decât versiunea curentă a clientului (${baselineVersion.major}.${baselineVersion.minor}.${baselineVersion.month}.${baselineVersion.day}). Downgrade-ul este blocat!`;
    }
    return "";
  }, [currentEnteredVersion, baselineVersion]);

  // Connect to SSE stream for logs & status updates
  const connectBuildStream = useCallback((buildId: string, onComplete: (success: boolean, projectInfo?: TAgentProjectInfo) => void) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `/rest/api/swsbuilder/v2/moa/build-stream?id=${buildId}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;
    let completed = false;
    let latestProjectInfo: TAgentProjectInfo | undefined;

    const finish = (success: boolean) => {
      if (completed) {
        return;
      }
      completed = true;
      clearInterval(statusFallback);
      onComplete(success, latestProjectInfo);
      es.close();
    };

    const isTerminalStatus = (status?: string) => {
      const normalized = status?.toLowerCase();
      return normalized === "completed" || normalized === "failed" || normalized === "cancelled";
    };

    const isSuccessStatus = (status?: string) => status?.toLowerCase() === "completed";

    const statusFallback = window.setInterval(async () => {
      try {
        const res = await fetch(`/rest/api/swsbuilder/v2/moa/job-status?id=${buildId}`);
        if (!res.ok) {
          return;
        }
        const job = await res.json();
        if (job.projectInfo) {
          latestProjectInfo = job.projectInfo;
        }
        if (isTerminalStatus(job.status)) {
          finish(isSuccessStatus(job.status));
        }
      } catch (e) {
        console.error("Failed to poll job status:", e);
      }
    }, 2000);

    es.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "INIT") {
          if (msg.job?.projectInfo) {
            latestProjectInfo = msg.job.projectInfo;
          }
          if (isTerminalStatus(msg.job?.status)) {
            finish(isSuccessStatus(msg.job?.status));
          }
        } else if (msg.type === "LOG") {
          setConsoleLogs(prev => prev + msg.text);
        } else if (msg.type === "PROJECT_INFO") {
          latestProjectInfo = msg.projectInfo;
        } else if (msg.type === "STATUS") {
          if (msg.payload?.projectInfo) {
            latestProjectInfo = msg.payload.projectInfo;
          }
          if (msg.payload?.job?.projectInfo) {
            latestProjectInfo = msg.payload.job.projectInfo;
          }
          const { status } = msg.payload;
          if (isTerminalStatus(status)) {
            finish(isSuccessStatus(status));
          }
        }
      } catch (e) {
        console.error("Failed to parse SSE event:", e);
      }
    };

    es.onerror = () => {
      console.warn("SSE stream connection closed or error occurred.");
    };
  }, []);

  // Poll job status as fallback or utility helper
  const pollJobUntilDone = (buildId: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`/rest/api/swsbuilder/v2/moa/job-status?id=${buildId}`);
          if (res.status === 200) {
            const data = await res.json();
            if (data.status === "completed") {
              clearInterval(interval);
              resolve(true);
            } else if (data.status === "failed" || data.status === "cancelled") {
              clearInterval(interval);
              resolve(false);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }, 2000);
    });
  };

  // SVN update & project initialization (Load project)
  const handleLoadProject = async () => {
    if (whatsNewGenerating || buildStatus === "RUNNING" || loadingProject) {
      return;
    }
    if (!clientCode) {
      notify({
        message: "Niciun client selectat în arborele OrgMan!",
        type: "warning",
        displayTime: 3500,
        width: 420
      }, {
        position: "top center",
        direction: "down-push"
      });
      return;
    }

    const isDevelopment = process.env.NODE_ENV === "development";

    setLoadingProject(true);
    setActiveTab("logs");
    setConsoleLogs("");
    appendDebug("LoadProject/context", {
      clientCode,
      clientFriendlyName,
      nodeEnv: process.env.NODE_ENV,
      agentConnected,
      currentVersion: `${currentEnteredVersion.major}.${currentEnteredVersion.minor}.${currentEnteredVersion.month}.${currentEnteredVersion.day}`
    });

    try {
      // 1. Run SVN update on JGAP server (Datacenter)
      if (isDevelopment) {
        setConsoleLogs(prev => prev + `[Dev] Se sare peste SVN Update pe JGAP server (Datacenter). SVN-ul obligatoriu rulează pe build machine.\n`);
      } else {
        setLoadingStatus("Rulare SVN Update pe JGAP server (Datacenter)...");
        setConsoleLogs(prev => prev + `[Server] Rulare SVN Update pe JGAP server (Datacenter) pentru SQL scripturi...\n`);
        try {
          const serverSvnRes = await fetch("/rest/api/swssvn/");
          if (!serverSvnRes.ok) {
            const errData = await serverSvnRes.json().catch(() => ({}));
            throw new Error(errData.message || `Status code: ${serverSvnRes.status}`);
          }
          setConsoleLogs(prev => prev + `[Server] SVN Update pe JGAP server finalizat cu succes.\n`);
        } catch (e) {
          console.error("Failed to run SVN update on JGAP server:", e);
          setConsoleLogs(prev => prev + `[Warning] Nu s-a putut actualiza SVN pe JGAP server (Datacenter): ${e instanceof Error ? e.message : String(e)}. Continuăm cu SVN-ul obligatoriu de pe build machine.\n`);
        }
      }

      // 2. Run SVN update on Build Agent
      setLoadingStatus("Rulare SVN Update pe build server...");
      setConsoleLogs(prev => prev + `[Server] Pornire SVN Update pe build machine pentru ${clientCode}...\n`);
      
      const svnResponse = await fetch("/rest/api/swsbuilder/v2/moa/trigger-svn-update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientCode })
      });
      appendDebug("trigger-svn-update/response", {
        status: svnResponse.status,
        ok: svnResponse.ok
      });

      if (svnResponse.status === 503) {
        setConsoleLogs("[Server] Eroare: Agentul local de build este deconectat.\n");
        setLoadingProject(false);
        return;
      }

      const svnData = await svnResponse.json();
      appendDebug("trigger-svn-update/body", svnData);
      if (svnData.success && svnData.buildId) {
        connectBuildStream(svnData.buildId, async (success, agentProjectInfo) => {
          let loadedBuildFilesCount = 0;
          let versionWasLoaded = false;

          appendDebug("build-stream/completed", {
            buildId: svnData.buildId,
            success,
            hasAgentProjectInfo: !!agentProjectInfo,
            agentProjectInfo: agentProjectInfo || null
          });
          if (!success) {
            setConsoleLogs(prev => prev + "[Error] SVN Update pe build machine a eşuat. Verificaţi că SVN există şi rulează în A:\\Proiecte D7\\Jurnal Gold 1.14\\.\n");
            setLoadingStatus("SVN Update pe build machine a eşuat.");
            setLoadingProject(false);
            return;
          }

          setConsoleLogs(prev => prev + "[Server] SVN Update pe build machine finalizat cu succes.\n");

          // 2. Load Project Files
          setLoadingStatus("Se încarcă fişierele din scriptul de build...");
          try {
            const nextBuildFiles = agentProjectInfo?.buildFiles || [];

            appendDebug("agent-project-info/files", {
              setupsDir: agentProjectInfo?.setupsDir,
              clientSwscPath: agentProjectInfo?.clientSwscPath,
              buildpathName: agentProjectInfo?.buildpathName,
              versionPath: agentProjectInfo?.versionPath,
              buildFilesCount: nextBuildFiles.length,
              firstBuildFiles: nextBuildFiles.slice(0, 5)
            });

            if (nextBuildFiles.length === 0) {
              setConsoleLogs(prev => prev + `[Error] Builder2 agent nu a trimis lista de fişiere din .swsc. Agentul trebuie să citească .swsc local şi să trimită projectInfo.buildFiles.\n`);
            }

            if (nextBuildFiles.length > 0) {
              setBuildFiles(nextBuildFiles);
              loadedBuildFilesCount = nextBuildFiles.length;
              appendDebug("build-files/loaded", {
                source: "builder-agent-project-info",
                count: nextBuildFiles.length,
                firstItems: nextBuildFiles.slice(0, 5)
              });
            } else {
              setBuildFiles([]);
              setConsoleLogs(prev => prev + `[Warning] Nu s-au găsit fişiere sau software-uri configurate pentru ${clientCode}.\n`);
            }
          } catch (e) {
            console.error("Failed to load build files:", e);
            setConsoleLogs(prev => prev + `[Warning] Nu s-a putut încărca lista de fişiere: ${e instanceof Error ? e.message : String(e)}.\n`);
          }

          // 3. Load baseline version details from last deployed setup
          setLoadingStatus("Se preia versiunea curentă...");
          try {
            const agentVersion = agentProjectInfo?.version;
            const currentVerStr = agentVersion
              ? `${agentVersion.major}.${agentVersion.minor}.${agentVersion.month}.${agentVersion.day}`
              : `${currentEnteredVersion.major}.${currentEnteredVersion.minor}.${currentEnteredVersion.month}.${currentEnteredVersion.day}`;
            appendDebug("agent-project-info/version", {
              versionPath: agentProjectInfo?.versionPath,
              version: agentVersion || null,
              fallbackVersion: currentVerStr
            });
            const parts = currentVerStr.split(".");
            if (parts.length === 4) {
              const baseVer = {
                major: parseInt(parts[0]) || 1,
                minor: parseInt(parts[1]) || 16,
                month: parseInt(parts[2]) || 1,
                day: parseInt(parts[3]) || 1
              };
              setBaselineVersion(baseVer);
              // Pre-fill version inputs with current version numbers
              setVersionMajor(baseVer.major);
              setVersionMinor(baseVer.minor);
              setVersionMonth(baseVer.month);
              setVersionDay(baseVer.day);
              setVersionLoaded(true);
              versionWasLoaded = true;
              appendDebug("version/loaded", {
                source: agentVersion?.source || (agentVersion ? "builder-agent-version-inc" : "current-ui-default"),
                version: currentVerStr,
                versionLoaded: true
              });
              if (!agentVersion) {
                setConsoleLogs(prev => prev + `[Warning] Builder2 agent nu a trimis versiunea din Version.inc. Versiunea curentă rămâne editabilă.\n`);
              }
            }
          } catch (e) {
            console.error("Failed to load baseline version:", e);
            setVersionLoaded(true);
            setConsoleLogs(prev => prev + `[Warning] Nu s-a putut citi versiunea deployată: ${e instanceof Error ? e.message : String(e)}. Versiunea curentă rămâne editabilă.\n`);
          }



          appendDebug("LoadProject/final-state", {
            loadedFiles: loadedBuildFilesCount,
            versionLoaded: versionWasLoaded,
            startProcessRequires: {
              loadingProject: false,
              versionWarning: !!versionWarning,
              agentConnected
            }
          });
          setConsoleLogs(prev => prev + `[Server] Proiectul ${clientCode} a fost încărcat cu succes.\n`);
          setActiveTab("progress");
          setLoadingProject(false);
        });
      } else {
        setConsoleLogs("[Server] Eroare la trimiterea comenzii de SVN update.\n");
        setLoadingProject(false);
      }
    } catch (e) {
      setConsoleLogs(prev => prev + `[Error] Failed: ${e}\n`);
      setLoadingProject(false);
    }
  };

  // Trigger Build Process
  const handleStartProcess = async () => {
    if (whatsNewGenerating || buildStatus === "RUNNING" || loadingProject) {
      return;
    }
    if (!clientCode) return;
    if (versionWarning) return;

    const description = whatsNewDescription.trim();
    if (!description) {
      setActiveTab("whatsnew");
      notify({
        message: `Descrierea WHATSNEW pentru ${currentVersionCode} este obligatorie.`,
        type: "error",
        displayTime: 4000,
        width: 480
      }, {
        position: "top center",
        direction: "down-push"
      });
      return;
    }

    setWhatsNewSaving(true);
    try {
      const descriptionChanged =
        (whatsNewEntry?.description || "").trim() !== description;
      const savedEntry = await runProductEvolutionApi<
        TWhatsNewEntry,
        TWhatsNewEntry
      >(saveWhatsNewRequest, {
        ...(whatsNewEntry || {}),
        versionCode: currentVersionCode,
        description,
        statusCode: whatsNewEntry?.statusCode || "DRAFT",
        sourceCode:
          generatedDescription === description && !generatedWasEdited
            ? "AI_SVN"
            : descriptionChanged
              ? "MANUAL"
              : whatsNewEntry?.sourceCode || "MANUAL"
      });
      setWhatsNewEntry(savedEntry);
      setWhatsNewDescription(savedEntry.description);
      setWhatsNewError("");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setWhatsNewError(message);
      setActiveTab("whatsnew");
      notify({
        message: `WHATSNEW nu a putut fi salvat: ${message}`,
        type: "error",
        displayTime: 5000,
        width: 520
      }, {
        position: "top center",
        direction: "down-push"
      });
      return;
    } finally {
      setWhatsNewSaving(false);
    }

    setConsoleLogs("");
    setBuildStatus("RUNNING");
    setActiveTab("logs");

    try {
      const response = await fetch("/rest/api/swsbuilder/v2/moa/trigger-build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientCode,
          incrementVersion: true,
          runSvnUpdate: false, // already ran on Load project
          customVersion: currentEnteredVersion
        })
      });

      if (response.status === 503) {
        setConsoleLogs("[Server] Eroare: Agentul local de build este offline.\n");
        setBuildStatus("FAILED");
        return;
      }

      const resData = await response.json();
      if (resData.success && resData.buildId) {
        setActiveBuildId(resData.buildId);
        connectBuildStream(resData.buildId, (success) => {
          if (success) {
            setBuildStatus("SUCCESS");
            setWhatsNewEntry((entry) =>
              entry ? { ...entry, statusCode: "RELEASED" } : entry
            );
            notify({
              message: "Procesul de build şi deploy a fost finalizat cu succes!",
              type: "success",
              displayTime: 4000,
              width: 480
            }, {
              position: "top center",
              direction: "down-push"
            });
          } else {
            setBuildStatus("FAILED");
          }
        });
      } else {
        setConsoleLogs(`[Server] ${resData.error || "Eroare la iniţierea build-ului."}\n`);
        setBuildStatus("FAILED");
      }
    } catch (e) {
      setConsoleLogs(prev => prev + `[Error] Failed to connect: ${e}\n`);
      setBuildStatus("FAILED");
    }
  };

  // Cancel build / Reset UI
  const handleCancelProcess = async () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    setBuildStatus("IDLE");
    setConsoleLogs(prev => prev + "\n[Server] Procesul a fost anulat de către utilizator local.\n");

    if (activeBuildId) {
      try {
        await fetch("/rest/api/swsbuilder/v2/moa/cancel-build", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: activeBuildId })
        });
      } catch (e) {
        console.error("Failed to send cancel command to server:", e);
      }
    }
  };

  // Parse console logs to dynamically extract compiler status of each file in real-time
  const parsedFileStatuses = useMemo(() => {
    const statuses: { [key: string]: "idle" | "loading" | "success" | "failure" } = {};
    if (buildStatus === "IDLE") return statuses;

    const lines = consoleLogs.split("\n");
    let activeFile: string | null = null;
    let failedFile: string | null = null;

    for (const line of lines) {
      if (line.includes("Processing file:")) {
        const match = line.match(/Processing file:\s*(.*)/i);
        if (match && match[1]) {
          const fullPath = match[1].trim();
          // Find if there is a match in our project file names
          const swscMatch = buildFiles.find(f => fullPath.toLowerCase().endsWith(f.path.toLowerCase()));
          if (swscMatch) {
            const fileName = swscMatch.path;
            if (activeFile && activeFile !== fileName && statuses[activeFile] === "loading") {
              statuses[activeFile] = "success";
            }
            activeFile = fileName;
            statuses[fileName] = "loading";
          }
        }
      }
      if (line.includes("failed:") || line.includes("Failed to start") || line.includes("Error]")) {
        if (activeFile) {
          statuses[activeFile] = "failure";
          failedFile = activeFile;
        }
      }
    }

    if (buildStatus === "SUCCESS" && activeFile && !failedFile) {
      statuses[activeFile] = "success";
      // Ensure all compile files are updated to success if they got initialized
      buildFiles.forEach(f => {
        if (statuses[f.path] === "loading") {
          statuses[f.path] = "success";
        }
      });
    }

    if (buildStatus === "FAILED" && activeFile) {
      statuses[activeFile] = "failure";
    }

    return statuses;
  }, [consoleLogs, buildStatus, buildFiles]);

  if (!clientCode) {
    return (
      <div className="builder-layout" style={{ justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <i className="dx-icon dx-icon-tips" style={{ fontSize: "48px", color: "#94a3b8" }}></i>
        <div style={{ marginTop: "15px", fontSize: "16px", color: "#64748b", fontWeight: 600 }}>
          Selectaţi un client din structura OrgMan pentru a accesa modulul de Builder.
        </div>
      </div>
    );
  }

  return (
    <div className="builder-layout">
      {/* Header section with agent connection status */}
      <div className="builder-header">
        <div>
          <h1>Builder - {clientFriendlyName || clientCode}</h1>
          <div className="subtitle-premium">Compilare automatizată şi deploy distribuiri client</div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="agent-badge">
            <span className={`badge-dot ${agentConnected ? "online" : "offline"}`}></span>
            <span>{agentConnected ? "Agent Online" : "Agent Offline"}</span>
          </div>
        </div>
      </div>

      {/* Version and Action Controls */}
      <div className="version-boxes-container">
        <div className="version-box">
          <span>Version Major</span>
          <NumberBox
            value={versionMajor}
            min={0}
            showSpinButtons={true}
            onValueChanged={(e) => setVersionMajor(e.value || 0)}
            disabled={buildStatus === "RUNNING" || loadingProject || whatsNewGenerating || !versionLoaded}
          />
        </div>
        <div className="version-box">
          <span>Version Minor</span>
          <NumberBox
            value={versionMinor}
            min={0}
            showSpinButtons={true}
            onValueChanged={(e) => setVersionMinor(e.value || 0)}
            disabled={buildStatus === "RUNNING" || loadingProject || whatsNewGenerating || !versionLoaded}
          />
        </div>
        <div className="version-box">
          <span>Version Month</span>
          <NumberBox
            value={versionMonth}
            min={1}            
            showSpinButtons={true}
            onValueChanged={(e) => setVersionMonth(e.value || 1)}
            disabled={buildStatus === "RUNNING" || loadingProject || whatsNewGenerating || !versionLoaded}
          />
        </div>
        <div className="version-box">
          <span>Version Day</span>
          <NumberBox
            value={versionDay}
            min={1}
            showSpinButtons={true}
            onValueChanged={(e) => setVersionDay(e.value || 1)}
            disabled={buildStatus === "RUNNING" || loadingProject || whatsNewGenerating || !versionLoaded}
          />
        </div>

        <div className="action-buttons">
          <Button
            text="Load project"
            icon="refresh"
            stylingMode="outlined"
            onClick={handleLoadProject}
            disabled={
              buildStatus === "RUNNING" ||
              loadingProject ||
              whatsNewGenerating ||
              !agentConnected
            }
            style={{ borderColor: "#10b981", color: "#10b981" }}
          />

          {buildStatus === "RUNNING" ? (
            <Button
              text="CANCEL PROCESS"
              icon="close"
              type="danger"
              stylingMode="contained"
              onClick={handleCancelProcess}
              style={{ backgroundColor: "#ef4444", color: "#ffffff", borderColor: "#ef4444" }}
            />
          ) : (
            <Button
              text="START PROCESS"
              icon="chevronright"
              type="success"
              stylingMode="contained"
              onClick={handleStartProcess}
              disabled={loadingProject || !!versionWarning || buildFiles.length === 0 || !agentConnected || whatsNewLoading || whatsNewSaving || whatsNewGenerating || !whatsNewDescription.trim()}
              style={{ backgroundColor: "#10b981", color: "#ffffff" }}
            />
          )}
        </div>
      </div>

      {/* Validation errors */}
      <div>

        {versionWarning && (
          <div className="version-warning-alert">
            <i className="dx-icon dx-icon-warning" style={{ fontSize: "16px" }}></i>
            <span>{versionWarning}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="builder-tabs">
        <button
          className={`tab-btn-simple ${activeTab === "progress" ? "active" : ""}`}
          onClick={() => setActiveTab("progress")}
        >
          TAB PROGRESS
        </button>
        <button
          className={`tab-btn-simple ${activeTab === "logs" ? "active" : ""}`}
          onClick={() => setActiveTab("logs")}
        >
          Tab Process LOGS
        </button>
        <button
          className={`tab-btn-simple ${activeTab === "whatsnew" ? "active" : ""}`}
          onClick={() => setActiveTab("whatsnew")}
        >
          WHATSNEW *
        </button>
      </div>

      {/* Content Area */}
      <div className="tab-content-container">
        {activeTab === "progress" ? (
          <div className="progress-checklist-grid">
            <div className="checklist-header">
              <div style={{ textAlign: "center" }}>Stare</div>
              <div>Nume Fişier</div>
              <div style={{ textAlign: "right" }}>Locaţie</div>
            </div>
            {buildFiles.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                {loadingProject ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                    <div className="spinner-border text-success" role="status" style={{ width: "24px", height: "24px" }}></div>
                    <span>{loadingStatus}</span>
                  </div>
                ) : (
                  "Niciun fişier încărcat. Vă rugăm să apăsaţi pe butonul 'Load project'."
                )}
              </div>
            ) : (
              buildFiles.map((file, idx) => {
                const status = parsedFileStatuses[file.path] || "idle";
                return (
                  <div key={idx} className={`checklist-row ${status}`}>
                    <div className="status-dot-container">
                      <span className={`status-dot ${status}`}></span>
                    </div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{file.path}</div>
                    <div style={{ textAlign: "right", color: "#64748b", fontSize: "12px" }}>build_workspace</div>
                  </div>
                );
              })
            )}
          </div>
        ) : activeTab === "logs" ? (
          <div ref={consoleRef} className="console-wrapper">
            <pre className="console-text">
              {consoleLogs || "Consola inactiva. Apăsaţi pe 'Load project' sau 'START PROCESS'."}
            </pre>
          </div>
        ) : (
          <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
              <strong>Versiunea {currentVersionCode}</strong>
              <span>
                {whatsNewLoading
                  ? "Se încarcă..."
                  : whatsNewEntry?.statusCode === "RELEASED"
                    ? "Lansat"
                    : "Ciornă"}
                {(generatedDescription !== null
                  ? generatedDescription === whatsNewDescription && !generatedWasEdited
                  : whatsNewEntry?.sourceCode === "AI_SVN")
                  ? " · completat de AI"
                  : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "12px" }}>
              <Button
                text={whatsNewGenerating ? "Se generează..." : "Generează cu AI"}
                icon="preferences"
                type="default"
                stylingMode="contained"
                onClick={handleGenerateWhatsNew}
                disabled={
                  whatsNewGenerating ||
                  buildStatus === "RUNNING" ||
                  loadingProject ||
                  whatsNewLoading ||
                  whatsNewSaving ||
                  (whatsNewStartRequired && !whatsNewStartAt)
                }
              />
              {generatedDescription !== null ? (
                <Button
                  text="Renunță la propunere"
                  stylingMode="outlined"
                  onClick={() => {
                    setWhatsNewDescription(preGenerationDescription);
                    setWhatsNewGeneration(null);
                    setGeneratedDescription(null);
                    setGeneratedWasEdited(false);
                  }}
                  disabled={whatsNewGenerating || buildStatus === "RUNNING"}
                />
              ) : null}
              {whatsNewStartRequired ? (
                <DateBox
                  aria-label="Început interval WHATSNEW"
                  type="datetime"
                  value={whatsNewStartAt}
                  onValueChanged={(event) => setWhatsNewStartAt(event.value || null)}
                  max={new Date()}
                />
              ) : null}
            </div>
            <TextArea
              value={whatsNewDescription}
              height={260}
              autoResizeEnabled={false}
              placeholder="Descrieţi în limba română schimbările acestei versiuni."
              disabled={
                buildStatus === "RUNNING" ||
                loadingProject ||
                whatsNewLoading ||
                whatsNewSaving ||
                whatsNewGenerating
              }
              onValueChanged={(event) => {
                const value = event.value || "";
                if (generatedDescription !== null && value !== whatsNewDescription) {
                  setGeneratedWasEdited(true);
                }
                setWhatsNewDescription(value);
              }}
            />
            {whatsNewGeneration ? (
              <div style={{ marginTop: "14px" }}>
                <strong>Draft Source Coverage</strong>
                <div>
                  {whatsNewGeneration.coverage.map((coverage) => (
                    <span key={coverage.source} style={{ marginRight: "14px" }}>
                      {coverage.source}: {coverage.status}
                      {` · ${coverage.elapsedMs} ms · ${coverage.rawCount} înregistrări · ${coverage.candidateCount} candidați`}
                      {coverage.truncated ? " (trunchiat)" : ""}
                      {coverage.error
                        ? ` · ${coverage.error.code}: ${coverage.error.message}`
                        : ""}
                    </span>
                  ))}
                </div>
                {whatsNewGeneration.warnings.map((warning) => (
                  <div key={warning} className="version-warning-alert" style={{ marginTop: "8px" }}>
                    {warning}
                  </div>
                ))}
                {whatsNewGeneration.candidates.map((candidate) => (
                  <details key={candidate.id} style={{ marginTop: "8px" }}>
                    <summary>{candidate.summary}</summary>
                    {candidate.evidence.map((evidence) => (
                      <div key={`${candidate.id}-${evidence.reference}`}>
                        {evidence.url ? (
                          <a
                            href={evidence.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {evidence.source} · {evidence.reference}
                          </a>
                        ) : (
                          <>{evidence.source} · {evidence.reference}</>
                        )}
                        {evidence.repository ? ` · ${evidence.repository}` : ""}
                        {evidence.labels?.map((label) => (
                          <span key={label} style={{ marginLeft: "8px" }}>
                            {label}
                          </span>
                        ))}
                        {evidence.paths?.map((sourcePath) => (
                          <div key={sourcePath}>{sourcePath}</div>
                        ))}
                      </div>
                    ))}
                  </details>
                ))}
              </div>
            ) : null}
            {whatsNewError ? (
              <div className="version-warning-alert" style={{ marginTop: "12px" }}>
                <i className="dx-icon dx-icon-warning"></i>
                <span>{whatsNewError}</span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
